<template>
  <div class="card-wrapper">
    <BmCard borderless disable-padding>
      <div :class="['animated-card', !isAnimationActive && 'stop-animations']">
        <div class="card-content">
          <h2>
            {{ title }}
          </h2>
          <div class="tracking-form">
            <BmInput
              id="postcode-input"
              :cancelIconAltText="$t('generic.cancel')"
              :label="$t('generic.postcode')"
              :on-input="onPostcodeChange"
              :type="
                configGetter($rttI18n.tenantId, themeStore.themeId)
                  .postCodeTextInput
                  ? 'text'
                  : 'tel'
              "
              :value="postcodeInputValue"
              class="postcode-input"
              @keyup.enter="() => consent && loadTrackingData()"
            />
            <BmCheckbox
              v-if="
                !configGetter($rttI18n.tenantId, themeStore.themeId)
                  .gdprApproved
              "
              id="consent-checkbox"
              :is-checked="consent"
              :on-change="onConsentChange"
              :title="$rttI18n.tenantSpecificContent('consentDeclaration')"
            />
            <BmCheckbox
              v-if="
                $rttI18n.isGlsAtSelected &&
                !configGetter($rttI18n.tenantId, themeStore.themeId)
                  .gdprApproved
              "
              id="second-consent-checkbox"
              :is-checked="secondConsent"
              :on-change="onSecondConsentChange"
              :title="
                $rttI18n.tenantSpecificContent('secondConsentDeclaration')
              "
            />

            <BmButton
              :aria-label="$t('deliveryAddress.buttonLabel')"
              :disabled="isSubmitButtonDisabled"
              :loading="trackingDataStore.requestState === 'LOADING'"
              :on-click="loadTrackingData"
              class="track-button"
              size="large"
              type="primary"
              >{{ buttonLabel }}
            </BmButton>
          </div>
        </div>
      </div>
    </BmCard>
  </div>
</template>
<script setup>
import { useRoute, useRouter } from 'vue-router';
import { sendAnalytics } from '@/services/analytics.js';

import BmInput from '@/v2/design-system/components/Input/BmInput.vue';
import BmCheckbox from '@/v2/design-system/components/Checkbox/BmCheckbox.vue';
import BmCard from '@/v2/design-system/components/Card/BmCard.vue';
import BmButton from '@/v2/design-system/components/Button/BmButton.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { configGetter } from '@/config/configGetter.js';
import { useGdprStore } from '@/stores/gdpr.js';
import { useThemeStore } from '@/stores/theme.js';
import { getUtmSource } from '@/router/queryParam.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useAnimation } from '@/v2/composables/useAnimation.js';

const $t = inject('t');
const $rttI18n = inject('rttI18n');
const route = useRoute();
const router = useRouter();

const props = defineProps({
  trackingId: {
    type: String,
    required: true
  },
  onSubmit: {
    type: Function,
    required: false
  }
});

const consent = ref(undefined);
const secondConsent = ref(undefined);
const postcodeInputValue = ref(null);

const trackingDataStore = useTrackingDataStore();
const gdprStore = useGdprStore();
const themeStore = useThemeStore();
const { isAnimationActive, startTimedAnimation } = useAnimation();

const title = computed(() => {
  return trackingDataStore.isServiceTypePickup ||
    trackingDataStore.isServiceTypeCollection
    ? $t('postcodePage.pickupTitle')
    : $t('deliveryAddress.description');
});

const buttonLabel = computed(() => {
  return trackingDataStore.isServiceTypePickup ||
    trackingDataStore.isServiceTypeCollection
    ? $t('postcodePage.pickupButtonLabel')
    : $t('deliveryAddress.buttonLabel');
});

const onPostcodeChange = value => {
  postcodeInputValue.value = value;
};

const onConsentChange = ({ value }) => {
  consent.value = value;
  gdprStore.setGdprConsent({
    tenantId: $rttI18n.tenantId,
    value
  });
};

const onSecondConsentChange = ({ value }) => {
  secondConsent.value = value;
  gdprStore.setGdprConsent({
    tenantId: `${$rttI18n.tenantId}-ads`,
    value
  });
};

const isSubmitButtonDisabled = computed(() => {
  return !(postcodeInputValue.value?.length && !!consent.value);
});

const loadTrackingData = async () => {
  sendAnalytics(
    trackingDataStore.data,
    'clickStats',
    `postcode_track_button_clicked`,
    { isV2: true }
  );
  await trackingDataStore.loadAdditionalTrackingData({
    utmSource: getUtmSource(route),
    tenantId: $rttI18n.tenantId,
    postcode: postcodeInputValue.value,
    trackingId: props.trackingId
  });

  props.onSubmit && props.onSubmit();
};

onMounted(() => {
  startTimedAnimation();
  const gdprConsent =
    gdprStore.hasGdprConsent($rttI18n.tenantId) ||
    configGetter($rttI18n.tenantId, themeStore.themeId).gdprApproved;

  consent.value = gdprConsent;
  secondConsent.value = gdprConsent;

  trackingDataStore.$onAction(({ name, store, args, after }) => {
    if (name === 'loadAdditionalTrackingData') {
      after(async () => {
        if (store.requestState !== 'ERROR') {
          await router.push({
            name: 'previewRealTimeTracking',
            params: {
              countryOrTenantId: $rttI18n.tenantId,
              locale: $rttI18n.locale,
              postcode: postcodeInputValue.value,
              trackingId: props.trackingId
            }
          });
        }
      });
    }
  });
});
</script>
<style scoped>
h2 {
  color: var(--neutral-text-icon-label);
  font-family: var(--body-small-med-family), sans-serif;
  font-size: var(--body-small-med-size);
  font-style: normal;
  font-weight: var(--body-small-med-weight);
  line-height: var(--line-height-lead-sm);
  margin-bottom: var(--pad-space-sp-24);
}

.tracking-form {
  display: flex;
  flex-direction: column;
  gap: var(--pad-space-sp-24);
}

.track-button {
  width: 100%;
  justify-content: center;
}

@property --animation-angle {
  syntax: '<angle>';
  inherits: true;
  initial-value: 0turn;
}

@keyframes snake {
  to {
    --animation-angle: 1turn;
  }
}

.animated-card {
  border: var(--border-width-b-4) solid transparent;
  border-radius: var(--corner-radius-c-16);
  background-image: linear-gradient(var(--neutral-surface-subtle) 0 0),
    conic-gradient(
      from var(--animation-angle),
      transparent 25%,
      var(--primary-border-default) 42%,
      var(--primary-border-default) 58%,
      transparent 75%
    );
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
  animation: snake 3s linear infinite;
}

.card-content {
  padding: var(--pad-space-sp-20);
}
</style>
